import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ShoppingCart, TrendUp, Lightning, ArrowUp, CurrencyDollar, Eye, Link as LinkIcon
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';
import { FabricatedDataNotice } from '../components/brand/FabricatedDataNotice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });

export default function DTRIPage() {
  const [overview, setOverview] = useState(null);
  const [ig, setIg] = useState(null);
  const [yt, setYt] = useState(null);
  const [india, setIndia] = useState(null);
  const [boost, setBoost] = useState(null);
  const [igSplit, setIgSplit] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/dtri/overview`, auth()),
      axios.get(`${API}/dtri/instagram`, auth()),
      axios.get(`${API}/dtri/youtube`, auth()),
      axios.get(`${API}/dtri/india-marketplace`, auth()),
      axios.get(`${API}/dtri/boost-recommender`, auth()),
    ]).then(([o, i, y, ind, b]) => { setOverview(o.data); setIg(i.data); setYt(y.data); setIndia(ind.data); setBoost(b.data); });
  }, []);

  const kpi = overview?.kpis || {};

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="dtri-page">
        <FabricatedDataNotice reason="Discovery-to-Revenue has no connected commerce/social attribution source in this environment. Every value below is generated, not measured." />
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight">Discovery-to-Revenue Intelligence</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Instagram Reels + YouTube Shopping + India Marketplace — unified social commerce</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Purchases', value: kpi.total_purchases?.toLocaleString() || '0', color: 'var(--accent)', icon: ShoppingCart },
            { label: 'Purchase Intent', value: kpi.purchase_intent?.toLocaleString() || '0', color: 'var(--success)', icon: Eye },
            { label: 'Creator Revenue', value: `INR ${(kpi.revenue || 0).toLocaleString()}`, color: 'var(--warning)', icon: CurrencyDollar },
            { label: 'AI Citation Lift', value: `${kpi.citation_lift_pct || 0}%`, color: 'var(--accent)', icon: TrendUp },
          ].map(m => {
            const Icon = m.icon;
            return (
              <div key={m.label} className="nx-metric-card rounded-lg">
                <div className="flex items-center gap-1.5 mb-2"><Icon size={12} style={{ color: m.color }} weight="duotone" /><span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">{m.label}</span></div>
                <p className="text-2xl font-black font-mono" style={{ color: m.color }}>{m.value}</p>
              </div>
            );
          })}
        </div>

        {/* Velocity Chart */}
        {overview?.daily_velocity && (
          <div className="nx-metric-card rounded-lg">
            <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">7-Day Purchase Velocity</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={overview.daily_velocity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="day" tick={{ fill: 'var(--text-tertiary)', fontSize: 9 }} axisLine={false} />
                  <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 9 }} axisLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 10 }} />
                  <Bar dataKey="instagram" stackId="a" fill="var(--accent)" name="Instagram" />
                  <Bar dataKey="youtube" stackId="a" fill="var(--error)" name="YouTube" />
                  <Bar dataKey="meesho" stackId="a" fill="var(--warning)" name="Meesho" />
                  <Bar dataKey="flipkart" stackId="a" fill="var(--success)" name="Flipkart" />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <Tabs defaultValue="ig" className="space-y-4">
          <TabsList className="bg-[var(--surface)] border border-[var(--border)]">
            <TabsTrigger value="ig" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white text-xs">Instagram</TabsTrigger>
            <TabsTrigger value="yt" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white text-xs">YouTube</TabsTrigger>
            <TabsTrigger value="india" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white text-xs">India Marketplace</TabsTrigger>
            <TabsTrigger value="boost" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white text-xs">Boost Engine</TabsTrigger>
            <TabsTrigger value="chain" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white text-xs">Attribution Chain</TabsTrigger>
          </TabsList>

          {/* Instagram */}
          <TabsContent value="ig">
            {ig && (() => {
              const [langSplit, setLangSplit] = [false, (v) => {}]; // Will use state
              return (
              <div className="nx-metric-card rounded-md">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-sm font-bold text-[var(--text-primary)]">Instagram Reels Affiliate</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-[var(--text-tertiary)] font-mono">View:</span>
                    <button onClick={() => setIgSplit(false)} className={`px-2 py-1 text-[9px] font-mono rounded ${!igSplit ? 'bg-[var(--accent)] text-white' : 'bg-[var(--surface-sunken)] text-[var(--text-tertiary)]'}`}>All Languages</button>
                    <button onClick={() => setIgSplit(true)} className={`px-2 py-1 text-[9px] font-mono rounded ${igSplit ? 'bg-[var(--accent)] text-white' : 'bg-[var(--surface-sunken)] text-[var(--text-tertiary)]'}`}>Hindi vs English</button>
                  </div>
                </div>
                {!igSplit ? (
                  <table className="w-full text-[10px] font-mono border-collapse">
                    <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]"><th className="text-left p-1.5">Creator</th><th className="p-1.5">Tags</th><th className="p-1.5">Taps</th><th className="p-1.5">Purchases</th><th className="p-1.5">Revenue</th><th className="p-1.5">Language</th><th className="p-1.5">AI</th></tr></thead>
                    <tbody>
                      {ig.reels?.map(r => (
                        <tr key={r.reel_id} className="border-b border-[var(--border-light)]">
                          <td className="p-1.5 text-[var(--text-primary)]">{r.creator}</td>
                          <td className="p-1.5 text-center">{r.products_tagged}</td>
                          <td className="p-1.5 text-center">{r.tap_rate}%</td>
                          <td className="p-1.5 text-center text-[var(--success)]">{r.purchases}</td>
                          <td className="p-1.5 text-center">INR {r.revenue.toLocaleString()}</td>
                          <td className="p-1.5"><span className="nx-tag nx-tag-blue text-[8px]">{r.language}</span></td>
                          <td className="p-1.5 text-center">{r.ai_crawled ? <Eye size={10} className="text-[var(--success)] inline" weight="fill" /> : <span className="text-[var(--text-tertiary)]">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  /* Side-by-side Hindi vs English per creator */
                  <div>
                    <p className="text-[9px] text-[var(--warning)] font-mono mb-3">Comparison: same creator, different language content</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] text-[var(--accent)] font-mono uppercase tracking-wider mb-2 font-bold">Hindi Content</p>
                        {ig.reels?.filter(r => r.language === 'Hindi').map(r => (
                          <div key={r.reel_id} className="p-2 rounded border border-[var(--accent-border)] bg-[var(--accent-light)] mb-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-[var(--text-primary)]">{r.creator}</span>
                              <span className="text-[10px] font-mono text-[var(--success)]">{r.purchases} purchases</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-[9px] font-mono text-[var(--text-secondary)]">
                              <span>Tap: {r.tap_rate}%</span>
                              <span>INR {r.revenue.toLocaleString()}</span>
                              {r.ai_crawled && <span className="encrypted-badge">AI Crawled</span>}
                            </div>
                          </div>
                        ))}
                        {ig.reels?.filter(r => r.language === 'Hindi').length === 0 && <p className="text-[9px] text-[var(--text-tertiary)]">No Hindi content</p>}
                      </div>
                      <div>
                        <p className="text-[10px] text-[var(--text-secondary)] font-mono uppercase tracking-wider mb-2 font-bold">English Content</p>
                        {ig.reels?.filter(r => r.language === 'English').map(r => (
                          <div key={r.reel_id} className="p-2 rounded border border-[var(--border)] mb-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-[var(--text-primary)]">{r.creator}</span>
                              <span className="text-[10px] font-mono text-[var(--success)]">{r.purchases} purchases</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-[9px] font-mono text-[var(--text-secondary)]">
                              <span>Tap: {r.tap_rate}%</span>
                              <span>INR {r.revenue.toLocaleString()}</span>
                              {r.ai_crawled && <span className="encrypted-badge">AI Crawled</span>}
                            </div>
                          </div>
                        ))}
                        {ig.reels?.filter(r => r.language === 'English').length === 0 && <p className="text-[9px] text-[var(--text-tertiary)]">No English content</p>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              );
            })()}
          </TabsContent>

          {/* YouTube */}
          <TabsContent value="yt">
            {yt && (() => {
              const shorts = yt.videos?.filter(v => v.type === 'short') || [];
              const longs = yt.videos?.filter(v => v.type === 'long') || [];
              const shortsTotal = shorts.reduce((s, v) => s + v.purchases, 0);
              const longsTotal = longs.reduce((s, v) => s + v.purchases, 0);
              const shortsAOV = shorts.length ? Math.round(shorts.reduce((s, v) => s + v.revenue, 0) / Math.max(shortsTotal, 1)) : 0;
              const longsAOV = longs.length ? Math.round(longs.reduce((s, v) => s + v.revenue, 0) / Math.max(longsTotal, 1)) : 0;
              const shortsDelayed = shorts.length ? Math.round(shorts.reduce((s, v) => s + v.delayed_conversions, 0) / shorts.length * 100 / Math.max(shortsTotal, 1)) : 0;
              const longsDelayed = longs.length ? Math.round(longs.reduce((s, v) => s + v.delayed_conversions, 0) / longs.length * 100 / Math.max(longsTotal, 1)) : 0;
              const chartData = [
                { metric: 'Purchases', Shorts: shortsTotal, 'Long-form': longsTotal },
                { metric: 'Avg Order Value', Shorts: shortsAOV, 'Long-form': longsAOV },
                { metric: 'Delayed Conv %', Shorts: shortsDelayed, 'Long-form': longsDelayed },
                { metric: 'AI Citation Rate', Shorts: Math.round(Math.random() * 15 + 5), 'Long-form': Math.round(Math.random() * 25 + 10) },
              ];
              return (
              <div className="space-y-4">
                <div className="nx-metric-card rounded-md">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-display text-sm font-bold text-[var(--text-primary)]">YouTube Shopping — Shorts vs Long-form</h3>
                    <span className="text-[9px] text-[var(--text-tertiary)] font-mono">30-day attribution window</span>
                  </div>
                  <p className="text-[10px] text-[var(--warning)] mb-4">Shorts = discovery engine, Long-form = conversion engine</p>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                        <XAxis dataKey="metric" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} axisLine={false} />
                        <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 9 }} axisLine={false} />
                        <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 11 }} />
                        <Bar dataKey="Shorts" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Long-form" fill="var(--success)" radius={[4, 4, 0, 0]} />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Detailed table below */}
                <div className="nx-metric-card rounded-md">
                  <h4 className="text-xs font-bold text-[var(--text-primary)] mb-3">Full Video Performance</h4>
                  <table className="w-full text-[10px] font-mono border-collapse">
                    <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]"><th className="text-left p-1.5">Creator</th><th className="p-1.5">Type</th><th className="p-1.5">Purchases</th><th className="p-1.5">Delayed</th><th className="p-1.5">Revenue</th><th className="p-1.5">Merchant</th><th className="text-left p-1.5">Keywords</th></tr></thead>
                    <tbody>
                      {yt.videos?.map(v => (
                        <tr key={v.video_id} className="border-b border-[var(--border-light)]">
                          <td className="p-1.5 text-[var(--text-primary)]">{v.creator}</td>
                          <td className="p-1.5"><span className={`nx-tag text-[8px] ${v.type === 'short' ? 'nx-tag-blue' : 'nx-tag-green'}`}>{v.type}</span></td>
                          <td className="p-1.5 text-center text-[var(--success)]">{v.purchases}</td>
                          <td className="p-1.5 text-center text-[var(--warning)]">{v.delayed_conversions}</td>
                          <td className="p-1.5 text-center">INR {v.revenue.toLocaleString()}</td>
                          <td className="p-1.5"><span className="nx-tag nx-tag-green text-[8px]">{v.merchant}</span></td>
                          <td className="p-1.5 text-[var(--text-secondary)]">{v.transcript_keywords?.join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              );
            })()}
          </TabsContent>

          {/* India */}
          <TabsContent value="india">
            {india && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(india.platforms || {}).map(([mp, d]) => (
                  <div key={mp} className="nx-metric-card rounded-lg">
                    <h4 className="font-display text-sm font-bold text-[var(--text-primary)] capitalize mb-2">{mp.replace('_', ' ')}</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div><p className="text-[9px] text-[var(--text-tertiary)]">Purchases</p><p className="text-lg font-bold text-[var(--success)] font-mono">{d.purchases}</p></div>
                      <div><p className="text-[9px] text-[var(--text-tertiary)]">Revenue</p><p className="text-lg font-bold text-[var(--text-primary)] font-mono">INR {d.revenue.toLocaleString()}</p></div>
                      <div><p className="text-[9px] text-[var(--text-tertiary)]">Commission</p><p className="text-sm font-bold text-[var(--accent)] font-mono">{d.avg_commission}%</p></div>
                      <div><p className="text-[9px] text-[var(--text-tertiary)]">Best Format</p><p className="text-sm text-[var(--text-primary)]">{d.best_format}</p></div>
                    </div>
                    <div className="flex gap-1 mt-2">{d.top_categories?.map(c => <span key={c} className="nx-tag nx-tag-blue text-[8px]">{c}</span>)}</div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Boost */}
          <TabsContent value="boost">
            {boost && (
              <div className="space-y-3">
                {boost.recommendations?.map(r => (
                  <div key={r.content_id} className="nx-metric-card rounded-lg flex items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-[var(--text-primary)] font-bold">{r.creator}</span>
                        <span className="nx-tag nx-tag-blue text-[8px]">{r.content_type}</span>
                        <span className="text-[9px] text-[var(--success)] font-mono">Conv: {r.organic_conv_rate}%</span>
                        <span className="text-[9px] text-[var(--accent)] font-mono">SoM: {r.som_contribution}%</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {Object.entries(r.projections || {}).map(([budget, p]) => (
                          <div key={budget} className="p-2 rounded border border-[var(--border-light)] text-center">
                            <p className="text-[9px] text-[var(--text-tertiary)] font-mono">INR {parseInt(budget).toLocaleString()}</p>
                            <p className="text-sm font-bold text-[var(--success)] font-mono">INR {p.revenue?.toLocaleString()}</p>
                            <p className="text-[8px] text-[var(--text-tertiary)]">{p.reach?.toLocaleString()} reach</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Button size="sm" className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[9px] h-7" onClick={() => toast.success('Sent to Paid Ads Agent')}>
                        <Lightning size={10} className="mr-1" />Paid Ads
                      </Button>
                      <Button size="sm" variant="outline" className="border-[var(--border)] text-[var(--text-secondary)] text-[9px] h-7" onClick={() => toast.success('Creator briefed')}>
                        Brief Creator
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Attribution Chain */}
          <TabsContent value="chain">
            <div className="nx-metric-card rounded-md">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                <LinkIcon size={16} className="inline mr-2 text-[var(--accent)]" weight="duotone" />Attribution Chain: Discovery → Purchase → Citation → Revenue
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 pb-4 chain-grid">
                {[
                  { stage: 1, name: 'Creator Post', icon: '📱', count: 12, trend: [3,5,2,4,6,3,4], platform: 'Instagram/YouTube', color: '#1A56DB' },
                  { stage: 2, name: 'Product Taps', icon: '👆', count: 1247, trend: [80,120,95,140,110,130,115], platform: 'In-app events', color: '#06B6D4' },
                  { stage: 3, name: 'Purchases', icon: '🛒', count: 89, trend: [8,12,10,15,11,14,13], platform: 'Affiliate confirmed', color: '#00D4AA' },
                  { stage: 4, name: 'AI Bot Crawl', icon: '🤖', count: 3, trend: [1,0,1,2,1,0,1], platform: 'GPTBot/PerplexityBot', color: '#F59E0B' },
                  { stage: 5, name: 'AI Citation', icon: '📝', count: 0, trend: [0,0,0,0,0,0,0], platform: 'LLM responses', color: '#64748B' },
                  { stage: 6, name: 'AI-Referred Visits', icon: '🔗', count: 0, trend: [0,0,0,0,0,0,0], platform: 'Citation clicks', color: '#64748B' },
                ].map((s) => {
                  const isEmpty = s.count === 0;
                  const maxTrend = Math.max(...s.trend, 1);
                  return (
                    <div key={s.stage} className={`p-4 rounded-lg border transition-all ${isEmpty ? 'border-[var(--border-light)] bg-[var(--surface-sunken)] opacity-40' : `border-[${s.color}]/20`}`}
                      style={{ borderColor: isEmpty ? 'var(--border-light)' : `${s.color}30`, background: isEmpty ? 'var(--surface-sunken)' : `${s.color}08` }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[9px] font-mono text-[var(--text-tertiary)]">Stage {s.stage}</span>
                        <span className="text-xs">{s.icon}</span>
                      </div>
                      <p className="text-[11px] text-[var(--text-primary)] font-medium mb-1">{s.name}</p>
                      <p className="text-2xl font-black font-mono mb-2" style={{ color: isEmpty ? 'var(--text-tertiary)' : s.color }}>
                        {isEmpty ? '—' : s.count.toLocaleString()}
                      </p>
                      {/* Mini sparkline */}
                      <div className="flex items-end gap-[2px] h-6 mb-2">
                        {s.trend.map((v, i) => (
                          <div key={i} className="flex-1 rounded-t-sm"
                            style={{ height: `${Math.max((v / maxTrend) * 100, isEmpty ? 8 : 15)}%`, background: isEmpty ? 'var(--border)' : `${s.color}60`, minHeight: '2px' }} />
                        ))}
                      </div>
                      <p className="text-[8px] text-[var(--text-tertiary)] font-mono">{s.platform}</p>
                      {isEmpty && <p className="text-[8px] text-[var(--warning)] font-mono mt-1 italic">Pending — awaiting upstream events</p>}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 text-[8px] text-[var(--text-tertiary)] font-mono">
                <span className="w-3 h-3 rounded bg-[var(--accent-light)] border border-[var(--accent-border)]" /> Active stage
                <span className="w-3 h-3 rounded bg-[var(--surface-sunken)] border border-[var(--border-light)] opacity-40 ml-2" /> Pending stage
                <span className="ml-auto">The gap between Purchase → AI Citation = the GEO Agent opportunity window</span>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Top Pairings */}
        {overview?.top_pairings && (
          <div className="nx-metric-card rounded-lg">
            <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Top Creator-Product Pairings This Week</h3>
            <table className="w-full text-[10px] font-mono border-collapse">
              <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]"><th className="text-left p-1.5">Creator</th><th className="text-left p-1.5">Product</th><th className="p-1.5">Platform</th><th className="p-1.5">Purchases</th><th className="p-1.5">Revenue</th></tr></thead>
              <tbody>
                {overview.top_pairings.map((p, i) => (
                  <tr key={i} className="border-b border-[var(--border-light)]">
                    <td className="p-1.5 text-[var(--text-primary)]">{p.creator}</td>
                    <td className="p-1.5 text-[var(--text-secondary)]">{p.product}</td>
                    <td className="p-1.5"><span className="nx-tag nx-tag-blue text-[8px]">{p.platform}</span></td>
                    <td className="p-1.5 text-center text-[var(--success)]">{p.purchases}</td>
                    <td className="p-1.5 text-center">INR {p.revenue.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
